import "./RestorationUnitsKPI.css";

import { ProjectIssuanceConstants } from "../../../../constants";
import { formatNumber, getInventoryKeyColours } from "../../../../utils";
import { GenericTooltip } from "../../../forms";

interface RestorationUnitsKPIProps {
  totalUnits: number;
  availableUnits: number;
  /** The listing's "available for sale" toggle. Off shows the headline total with no split. */
  showAvailable: boolean;
}

export const RESTORATION_UNIT_DEFINITION = "1 Unit = 30 m² for 12 months";

/**
 * The public project page's Restore 30x30 units tile (KAN-9935): ONE total figure split into
 * Allocated (Sold + Reserved) and Available, the public Directory's convention - the Sold / Reserved
 * split is Hub-only.
 *
 * Modelled on NutrientUnitsKPI, a single figure split proportionally into bands. The bands take the
 * Restoration family from getInventoryKeyColours - Allocated dark, Available light, as the Inventory
 * tab's chart and key draw them - so the tile and the tab cannot drift apart.
 */
export const RestorationUnitsKPI = ({
  totalUnits,
  availableUnits,
  showAvailable,
}: RestorationUnitsKPIProps): JSX.Element => {
  const colours = getInventoryKeyColours(ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_RESTORATION);
  const allocatedUnits = totalUnits - availableUnits;

  // Lighter shade first, as the KPI tiles beside this one order their bands.
  const bands = [
    { key: "Available", units: availableUnits, colour: colours.light },
    { key: "Allocated", units: allocatedUnits, colour: colours.dark },
  ];

  return (
    <div className="RestorationUnitsContainer">
      {showAvailable && (
        <div className="RestorationUnitsGraph">
          {bands.map(({ key, units, colour }) => (
            <GenericTooltip
              key={key}
              placement="right"
              content={
                <div className="RestorationUnitsTooltipContainer">
                  <p className="body3">{`${formatNumber(units)} ${key}`}</p>
                </div>
              }
            >
              {/*
                An empty share keeps its band as a 2px rule rather than disappearing, so a fully
                available project still reads as an Allocated / Available split. `totalUnits` guards
                the division.
              */}
              <div
                className={`RestorationUnitsGraphBand ${key} ${units > 0 ? "" : "RestorationUnitsGraphBand_empty"}`}
                style={{
                  backgroundColor: colour,
                  ...(units > 0 && totalUnits > 0 ? { flex: (units / totalUnits) * 100 } : {}),
                }}
              />
            </GenericTooltip>
          ))}
        </div>
      )}
      <div className="RestorationUnitsHeaderArea">
        <h2>{formatNumber(totalUnits)}</h2>
        <p className="body2">{ProjectIssuanceConstants.ISSUANCE_UNIT_MEASUREMENT_RESTORATION}</p>
        <p className="body3">{RESTORATION_UNIT_DEFINITION}</p>
      </div>
    </div>
  );
};
